import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, Search, X, MapPin, UsersRound,
  CalendarDays, Clock, ChevronLeft, ChevronRight,
  Eye, Trash2, CheckCircle2, FileText, Image,
  MessageSquare, RefreshCw, AlertTriangle, Download,
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

// Normalize any bool-like value: true, false, null, 'yes', 'no', 'Yes', 'No'
const normBool = (v) => v === true || (typeof v === 'string' && v.toLowerCase() === 'yes');

const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
});
const svgPts = (pts) => pts.map(p => `${p.x},${p.y}`).join(' ');

const safeUrl = (baseUrl, url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const fmtDate = (v, opts) => v ? new Date(v).toLocaleDateString('en-GB', opts || { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const fmtDateTime = (v) => v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null;

// ─── Custom Human SVG Icon ───────────────────────────────────────────────────
const WalkingHuman = ({ color = "#38BDF8", size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <circle cx="12" cy="5" r="2.5" fill={color} /> {/* Head */}
    <path d="M12 8v6"/>            {/* Body */}
    <path d="M12 14l-3.5 6"/>      {/* Left Leg */}
    <path d="M12 14l3.5 6"/>       {/* Right Leg */}
    <path d="M12 9l-4 3"/>         {/* Left Arm */}
    <path d="M12 9l4 3"/>          {/* Right Arm */}
  </svg>
);

// ─── PDF Generator ────────────────────────────────────────────────────────────
const generateJobPDF = async ({ detail, jobStrings, jobStringFlags, annotations, photos, baseUrl }) => {
  const [jsPDFmod, html2canvas] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);
  const jsPDF = jsPDFmod.default || jsPDFmod.jsPDF;
  const h2c   = html2canvas.default || html2canvas;

  const job = detail.job || {};
  const fsr = detail.fsr || null;

  // Build off-screen report node
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;font-family:DM Sans,Segoe UI,sans-serif;color:#111;';
  document.body.appendChild(wrap);

  const boolBadge = (val) =>
    `<span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;font-family:DM Mono,monospace;background:${val ? '#dcfce7' : '#fee2e2'};color:${val ? '#16a34a' : '#dc2626'};">${val ? '✓ Yes' : '✗ No'}</span>`;

  const row = (label, value) => value
    ? `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:9px 14px;font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;width:130px;vertical-align:top;">${label}</td><td style="padding:9px 14px;font-size:13px;color:#1e293b;font-weight:500;">${value}</td></tr>`
    : '';

  const section = (title, icon, content) =>
    `<div style="margin-bottom:24px"><div style="display:flex;align-items:center;gap:8px;padding:10px 16px;background:#f8fafc;border-left:4px solid #3b82f6;border-radius:0 8px 8px 0;margin-bottom:12px"><span style="font-size:15px">${icon}</span><span style="font-size:12px;font-weight:800;color:#1e293b;text-transform:uppercase;letter-spacing:0.1em">${title}</span></div>${content}</div>`;

  const tsRows = [
    { label: 'Reached Site',   val: job.reached_site_at   || detail.reached_site_at   },
    { label: 'Reached Panels', val: job.reached_panels_at || detail.reached_panels_at },
    { label: 'Work Started',   val: job.work_started_at   || detail.work_started_at   },
    { label: 'Work Finished',  val: job.work_finished_at  || detail.work_finished_at  },
    { label: 'Site Exited',    val: job.site_exited_at    || detail.site_exited_at    },
  ].filter(r => r.val);

  const flaggedStrings = jobStrings.filter(s => jobStringFlags.includes(s.id));

  const membersHtml = detail.members?.length
    ? `<table style="width:100%;border-collapse:collapse">
        ${detail.members.map(m => `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:9px 14px;font-size:13px;color:#1e293b;font-weight:600;">${m.firstname} ${m.lastname}</td><td style="padding:9px 14px;font-size:10px;font-weight:700;color:${m.role==='lead'?'#d97706':'#3b82f6'};font-family:DM Mono,monospace;text-transform:uppercase;">${m.role}</td><td style="padding:9px 14px;font-size:11px;color:#64748b;font-family:DM Mono,monospace;">${m.mobile||''}</td></tr>`).join('')}
      </table>`
    : '<p style="color:#94a3b8;font-size:12px;padding:0 14px">No team members assigned</p>';

  const fsrBoolFields = [
    { label: 'Panel Damage',      key: 'panel_damage'       },
    { label: 'Inverter Alarm',    key: 'inverter_alarm'     },
    { label: 'Potential Shading', key: 'potential_shading'  },
    { label: 'Rusting',           key: 'rusting'            },
    { label: 'Bird Dropping',     key: 'bird_dropping'      },
    { label: 'Moss & Debris',     key: 'mos_and_debris'     },
    { label: 'Earthing',          key: 'earthing'           },
  ];

  const fsrGridHtml = fsr
    ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:14px 0">
        ${fsrBoolFields.map(f => `<div style="background:#f8fafc;border-radius:8px;padding:10px 14px"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:5px">${f.label}</div>${boolBadge(normBool(fsr[f.key]))}</div>`).join('')}
      </div>`
    : '';

  const annotHtml = annotations.length
    ? `<table style="width:100%;border-collapse:collapse">
        ${annotations.map(a => `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px 14px;font-size:12px;color:#64748b;width:120px;font-weight:600">${a.point_label||'—'}</td><td style="padding:8px 14px;font-size:12px;color:#1e293b">${a.note||'—'}</td></tr>`).join('')}
      </table>`
    : '<p style="color:#94a3b8;font-size:12px;padding:0 14px">No field annotations</p>';

  wrap.innerHTML = `
    <!-- HEADER -->
    <div style="background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%);padding:28px 32px 22px;color:#fff;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between">
        <div>
          <div style="font-size:22px;font-weight:900;letter-spacing:0.05em;color:#fff">iNOVAA</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.45);letter-spacing:0.12em;text-transform:uppercase;margin-top:2px">Field Service Report</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:11px;color:rgba(255,255,255,0.40);font-family:DM Mono,monospace;">Generated</div>
          <div style="font-size:12px;font-weight:700;color:rgba(255,255,255,0.75);font-family:DM Mono,monospace;">${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</div>
        </div>
      </div>
      <div style="margin-top:20px;padding-top:16px;border-top:1px solid rgba(255,255,255,0.12)">
        <div style="font-size:20px;font-weight:800;color:#fff">${job.site_name||'—'}</div>
        <div style="font-size:12px;color:rgba(255,255,255,0.50);margin-top:4px">${job.client_name||''} · ${fmtDate(job.scheduled_date)}</div>
      </div>
      <div style="margin-top:14px">
        <span style="display:inline-block;padding:4px 14px;border-radius:20px;font-size:10px;font-weight:800;font-family:DM Mono,monospace;letter-spacing:0.08em;text-transform:uppercase;background:${job.status==='completed'?'rgba(34,197,94,0.25)':job.status==='in_progress'?'rgba(245,158,11,0.25)':'rgba(14,165,233,0.25)'};color:${job.status==='completed'?'#86efac':job.status==='in_progress'?'#fcd34d':'#7dd3fc'};border:1px solid ${job.status==='completed'?'rgba(34,197,94,0.40)':job.status==='in_progress'?'rgba(245,158,11,0.40)':'rgba(14,165,233,0.40)'}">
          ${(job.status||'').replace('_',' ')}
        </span>
      </div>
    </div>
    <!-- BODY -->
    <div style="padding:28px 32px">
      ${section('Job Details', '📋', `
        <table style="width:100%;border-collapse:collapse">
          ${row('Site',     job.site_name)}
          ${row('Client',   job.client_name)}
          ${row('Location', job.location)}
          ${row('Team',     job.team_name||'Unassigned')}
          ${row('Date',     fmtDate(job.scheduled_date))}
          ${row('Time',     job.scheduled_time)}
        </table>
      `)}
      ${detail.members?.length ? section('Team Members', '👷', membersHtml) : ''}
      ${tsRows.length ? section('Tracking Timestamps', '🕐', `
        <table style="width:100%;border-collapse:collapse">
          ${tsRows.map(r => row(r.label, fmtDateTime(r.val))).join('')}
        </table>
      `) : ''}
      ${flaggedStrings.length ? section('String Attention Flags', '⚠️', `
        <div style="display:flex;flex-wrap:wrap;gap:8px;padding:4px 14px">
          ${flaggedStrings.map(s => `<span style="padding:5px 14px;border-radius:20px;font-size:11px;font-weight:700;font-family:DM Mono,monospace;background:#fef2f2;color:#dc2626;border:1px solid #fecaca">⚠ ${s.label}</span>`).join('')}
        </div>
      `) : ''}
      ${fsr ? section('Field Service Report', '✅', `
        <table style="width:100%;border-collapse:collapse">
          ${row('Panels Cleaned',  fsr.panels_cleaned)}
          ${row('Submitted By',   fsr.submitted_by_name)}
          ${row('Submitted At',   fmtDateTime(fsr.submitted_at))}
          ${row('Cable Condition',fsr.cable_condition)}
          ${row('Cable Quantity', fsr.cable_quantity)}
          ${row('Panel Brand',    fsr.panel_brand)}
          ${row('Alarm Code',     fsr.alarm_code)}
          ${row('Shading Details',fsr.shading_details)}
        </table>
        <div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;padding:14px 14px 6px">Inspection Results</div>
        ${fsrGridHtml}
        ${fsr.observations ? `<div style="margin-top:10px;background:#f8fafc;border-radius:8px;padding:12px 14px"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">Observations</div><div style="font-size:13px;color:#1e293b;line-height:1.6">${fsr.observations}</div></div>` : ''}
        ${fsr.work_done    ? `<div style="margin-top:8px;background:#f8fafc;border-radius:8px;padding:12px 14px"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">Work Done</div><div style="font-size:13px;color:#1e293b;line-height:1.6">${fsr.work_done}</div></div>` : ''}
        ${fsr.signature_url ? `
          <div style="margin-top:14px;padding:14px;border:1px solid #e2e8f0;border-radius:8px;background:#fff">
            <div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:10px">Client Signature</div>
            <img src="${safeUrl(baseUrl, fsr.signature_url)}" crossorigin="anonymous" style="max-height:100px;max-width:260px;display:block;border:1px solid #e2e8f0;border-radius:6px;background:#fff"/>
          </div>` : ''}
      `) : `<div style="padding:14px;color:#94a3b8;font-size:12px">FSR not submitted yet.</div>`}
      ${section('Field Annotations', '🔧', annotHtml)}
      <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center">
        <span style="font-size:10px;color:#94a3b8;font-family:DM Mono,monospace">iNOVAA Platform · SoWash USA</span>
        <span style="font-size:10px;color:#94a3b8;font-family:DM Mono,monospace">Job #${job.id||'—'}</span>
      </div>
    </div>
  `;

  await new Promise(r => setTimeout(r, 300));
  const canvas = await h2c(wrap, {
    scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', width: 794, windowWidth: 794,
  });
  document.body.removeChild(wrap);

  const imgData  = canvas.toDataURL('image/png');
  const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pdfW     = pdf.internal.pageSize.getWidth();
  const pdfH     = pdf.internal.pageSize.getHeight();
  const imgH     = (canvas.height * pdfW) / canvas.width;
  let   yOffset  = 0;

  while (yOffset < imgH) {
    if (yOffset > 0) pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, -yOffset, pdfW, imgH, undefined, 'FAST');
    yOffset += pdfH;
  }

  const slug = (job.site_name || 'job').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const date = (job.scheduled_date || '').slice(0, 10);
  pdf.save(`NOVAA_Report_${slug}_${date}.pdf`);
};

// ─── GTA-style SLD Viewer for Admin ──────────────────────────────────────────
const AdminSLDViewer = ({
  diagram, photos, annotations, notes, strings, flaggedStrings,
  baseUrl, layerNotes, layerPhotos, layerAnnots, photosLoaded,
}) => {
  const [view,        setView]       = useState('map');
  const [activePoint, setActivePoint]= useState(null);
  const [navDir,      setNavDir]     = useState('fwd');
  const [mapPhase,    setMapPhase]   = useState('visible');
  const [photoPhase,  setPhotoPhase] = useState('hidden');
  const [zoomOrigin,  setZoomOrigin] = useState('50% 50%');
  const [hoveredPin,  setHoveredPin] = useState(null);
  const [isWalking,   setIsWalking]  = useState(false);

  // Slider position (0-100)
  const [sliderPos,   setSliderPos]  = useState(50);

  useEffect(() => {
    setView('map'); setMapPhase('visible'); setPhotoPhase('hidden');
    setActivePoint(null);
  }, [diagram]);

  const diagramUrl = diagram?.diagram_url ? safeUrl(baseUrl, diagram.diagram_url) : null;

  const pointList = useMemo(() => {
    if (!diagram?.points) return [];
    return diagram.points.map((pt, i) => {
      const pp = photos.filter(p => p.point_id === pt.id);
      return {
        id: pt.id, index: i + 1, label: pt.label,
        x_percent: pt.x_percent, y_percent: pt.y_percent,
        hasPhoto: pp.length > 0,
        before: pp.find(p => p.photo_type === 'before') || null,
        after:  pp.find(p => p.photo_type === 'after')  || null,
      };
    });
  }, [diagram, photos]);

  const curIdx    = pointList.findIndex(p => p.id === activePoint?.id);
  const canGoBack = curIdx > 0;
  const canGoFwd  = curIdx < pointList.length - 1;

  const getPinColor = (pointId) => {
    if (layerAnnots && annotations.some(a => a.point_id === pointId)) return '#EF4444';
    if (layerNotes  && notes?.some(n => n.point_id === pointId))       return '#F59E0B';
    if (layerPhotos && photos.some(p => p.point_id === pointId))       return '#22D3A5';
    return 'rgba(255,255,255,0.28)';
  };

  const getUrl = (p) => safeUrl(baseUrl, p?.photo_url);

  const handlePinClick = (pt) => {
    if (!pt.hasPhoto || mapPhase !== 'visible') return;
    setZoomOrigin(`${pt.x_percent ?? 50}% ${pt.y_percent ?? 50}%`);
    setMapPhase('zooming');
    setTimeout(() => {
      setMapPhase('hidden');
      setNavDir('fwd');
      setActivePoint(pt);
      setSliderPos(50);
      setPhotoPhase('entering');
      // Set walking animation duration
      setIsWalking(true);
      setTimeout(() => setIsWalking(false), 800);
      setTimeout(() => { setView('photo'); setPhotoPhase('visible'); }, 30);
    }, 650);
  };

  const handleBack = () => {
    if (photoPhase !== 'visible') return;
    setPhotoPhase('leaving');
    setTimeout(() => {
      setPhotoPhase('hidden');
      setView('map');
      setMapPhase('zoomback');
      setTimeout(() => setMapPhase('visible'), 600);
    }, 380);
  };

  const navigate = (idx, dir) => {
    setNavDir(dir);
    const next = pointList[idx];
    setActivePoint(next);
    setSliderPos(50);
    setIsWalking(true);
    setTimeout(() => setIsWalking(false), 800);
  };

  const mapStyle = () => {
    const base = { position:'absolute', inset:0, width:'100%', height:'100%', background:'#0D1120', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' };
    if (mapPhase === 'visible')  return { ...base, transform:'scale(1)',   opacity:1, filter:'blur(0)',  transition:'none' };
    if (mapPhase === 'zooming')  return { ...base, transformOrigin:zoomOrigin, transform:'scale(3)', opacity:0, filter:'blur(5px)', transition:'transform 0.65s cubic-bezier(0.4,0,0.2,1), opacity 0.65s ease, filter 0.65s ease' };
    if (mapPhase === 'hidden')   return { ...base, display:'none' };
    if (mapPhase === 'zoomback') return { ...base, transformOrigin:zoomOrigin, transform:'scale(1)',   opacity:1, filter:'blur(0)',  transition:'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease 0.1s' };
    return base;
  };

  const photoStyle = () => {
    const base = { position:'absolute', inset:0, background:'#0D1120', display:'flex', flexDirection:'row', overflow:'hidden' };
    if (photoPhase === 'hidden')   return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase === 'entering') return { ...base, opacity:0, transform:'scale(1.05)', transition:'none', pointerEvents:'none' };
    if (photoPhase === 'visible')  return { ...base, opacity:1, transform:'scale(1)',    transition:'opacity 0.45s ease, transform 0.45s cubic-bezier(0.4,0,0.2,1)', pointerEvents:'all' };
    if (photoPhase === 'leaving')  return { ...base, opacity:0, transform:'scale(1.05)', transition:'opacity 0.38s ease, transform 0.38s ease', pointerEvents:'none' };
    return base;
  };

  const ArrowBtn = ({ side, onClick, label }) => {
    const [hov, setHov] = useState(false);
    return (
      <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ position:'absolute', top:0, bottom:0, [side]:0, width:'15%', zIndex:30, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
          background: hov ? `linear-gradient(${side==='left'?'90':'270'}deg,rgba(0,0,0,0.55) 0%,transparent 100%)` : `linear-gradient(${side==='left'?'90':'270'}deg,rgba(0,0,0,0.20) 0%,transparent 100%)`,
          transition:'background 0.25s ease' }}>
        <div style={{ width:50, height:50, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
          background:hov?'rgba(255,255,255,0.22)':'rgba(0,0,0,0.42)', border:`2px solid ${hov?'#fff':'rgba(255,255,255,0.60)'}`,
          backdropFilter:'blur(6px)', color:'#fff', transition:'all 0.22s ease', transform:hov?'scale(1.13)':'scale(1)' }}>
          {side === 'left' ? <ChevronLeft size={22} strokeWidth={2.8}/> : <ChevronRight size={22} strokeWidth={2.8}/>}
        </div>
        {hov && (
          <div style={{ position:'absolute', bottom:24, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.82)', border:'1px solid rgba(255,255,255,0.14)', borderRadius:7, padding:'4px 10px', fontSize:11, fontWeight:700, fontFamily:'DM Mono,monospace', color:'#fff', whiteSpace:'nowrap', backdropFilter:'blur(6px)', animation:'asv-fadeUp 0.14s ease' }}>
            {side === 'left' ? `← ${label}` : `${label} →`}
          </div>
        )}
      </div>
    );
  };

  if (!diagram) return (
    <div className="md-empty"><div className="md-empty-icon"><Image size={24}/></div>No diagram for this site</div>
  );

  return (
    <div style={{ position:'relative', borderRadius:14, overflow:'hidden', background:'#0D1120', height:680, border:'1px solid rgba(255,255,255,0.07)', boxShadow:'0 10px 40px rgba(0,0,0,0.5)' }}>
      {/* ── MAP PHASE ── */}
      <div style={mapStyle()}>
        {diagramUrl ? (
          <div className="sld-image-container">
            <img src={diagramUrl} alt="Site diagram" className="gta-map-img" draggable={false}/>
            <svg className="mj-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
              {strings.map(s => {
                const pts = Array.isArray(s.points) ? s.points : [];
                if (pts.length < 3) return null;
                const isFlagged = flaggedStrings.includes(s.id);
                return (
                  <polygon key={s.id}
                    className={isFlagged ? 'mj-string-flagged-polygon' : ''}
                    points={svgPts(pts)}
                    fill={isFlagged ? `${s.color}35` : `${s.color}18`}
                    stroke={s.color}
                    strokeWidth={isFlagged ? '0.7' : '0.4'}
                    strokeOpacity={isFlagged ? '0.95' : '0.60'}
                  />
                );
              })}
            </svg>
            {strings.map(s => {
              const pts = Array.isArray(s.points) ? s.points : [];
              if (pts.length < 3) return null;
              const isFlagged = flaggedStrings.includes(s.id);
              const c = centroid(pts);
              return (
                <span key={`lbl-${s.id}`} className="mj-string-centroid-label" style={{ left:`${c.x}%`, top:`${c.y}%`, color:s.color }}>
                  {isFlagged ? '⚠ ' : ''}{s.label}
                </span>
              );
            })}
            {pointList.map(pt => {
              const isH    = hoveredPin === pt.id;
              const pinClr = getPinColor(pt.id);
              const clickable = pt.hasPhoto && photosLoaded;
              return (
                <div key={pt.id}
                  onMouseEnter={() => setHoveredPin(pt.id)}
                  onMouseLeave={() => setHoveredPin(null)}
                  onClick={() => clickable && handlePinClick(pt)}
                  style={{ position:'absolute', left:`${pt.x_percent ?? 50}%`, top:`${pt.y_percent ?? 50}%`, transform:'translate(-50%,-50%)', display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor:clickable?'pointer':'default', zIndex:isH?30:10 }}>
                  {isH && (
                    <div style={{ position:'absolute', bottom:'108%', left:'50%', transform:'translateX(-50%)', background:'rgba(8,12,24,0.92)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'5px 12px', fontSize:11, fontWeight:700, fontFamily:'DM Mono,monospace', color:'#E2E8F0', whiteSpace:'nowrap', backdropFilter:'blur(8px)', animation:'asv-fadeUp 0.14s ease', pointerEvents:'none' }}>
                      {pt.label || `Point ${pt.index}`}{pt.hasPhoto && <span style={{ color:'#38BDF8', marginLeft:6 }}>▶ View</span>}
                    </div>
                  )}
                  <div style={{ width:isH?42:32, height:isH?42:32, borderRadius:'50%', background: pinClr !== 'rgba(255,255,255,0.28)' ? (isH?`${pinClr}60`:`${pinClr}28`) : 'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.2s ease', animation: pt.hasPhoto ? 'asv-pinPulse 2.5s ease-in-out infinite' : 'none' }}>
                    <div style={{ width:isH?28:22, height:isH?28:22, borderRadius:'50%', background:pinClr, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, fontFamily:'DM Mono,monospace', color: pinClr === 'rgba(255,255,255,0.28)' ? 'rgba(0,0,0,0.55)' : '#080C18', boxShadow:isH?`0 0 0 3px rgba(255,255,255,0.3),0 0 18px ${pinClr}99`:'0 0 0 2px rgba(0,0,0,0.5),0 3px 8px rgba(0,0,0,0.4)', transition:'all 0.2s ease' }}>{pt.index}</div>
                  </div>
                  {pt.label && <div style={{ background:'rgba(8,12,24,0.88)', borderRadius:4, padding:'2px 6px', fontSize:9, fontWeight:700, fontFamily:'DM Mono,monospace', color:'#E2E8F0', whiteSpace:'nowrap', border:'1px solid rgba(255,255,255,0.10)', backdropFilter:'blur(4px)', pointerEvents:'none', opacity:isH?1:0.78, transition:'opacity 0.2s' }}>{pt.label}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color:'rgba(255,255,255,0.28)', display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:28 }}>🗺️</span><span style={{ fontWeight:600 }}>No diagram image</span>
          </div>
        )}
        <div style={{ position:'absolute', top:14, left:14, color:'#38BDF8', fontSize:12, fontWeight:800, letterSpacing:1, textTransform:'uppercase', background:'rgba(15,23,42,0.88)', padding:'5px 13px', borderRadius:8, border:'1px solid rgba(56,189,248,0.30)', zIndex:10 }}>
          SLD Map
        </div>
        {!photosLoaded && (
          <div style={{ position:'absolute', bottom:14, left:'50%', transform:'translateX(-50%)', color:'rgba(255,255,255,0.38)', fontSize:11, fontWeight:600, background:'rgba(0,0,0,0.58)', padding:'5px 14px', borderRadius:20, zIndex:10, whiteSpace:'nowrap' }}>
            Loading photos…
          </div>
        )}
        {photosLoaded && (
          <div style={{ position:'absolute', bottom:14, left:'50%', transform:'translateX(-50%)', color:'rgba(255,255,255,0.38)', fontSize:11, fontWeight:600, background:'rgba(0,0,0,0.58)', padding:'5px 14px', borderRadius:20, zIndex:10, whiteSpace:'nowrap', pointerEvents:'none' }}>
            Click a pin to view photos
          </div>
        )}
      </div>

      {/* ── PHOTO PHASE (SPLIT VIEW & SLIDER) ── */}
      <div style={photoStyle()}>
        {activePoint && (
          <>
            {/* LEFT: MAIN IMAGE AREA WITH SLIDER */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#050810' }}>
              <AnimatePresence initial={false} custom={navDir}>
                <motion.div
                  key={activePoint.id}
                  custom={navDir}
                  variants={{
                    enter:  (dir) => ({ scale:dir==='fade'?1:(dir==='fwd'?0.6:1.4), opacity:0, filter:'blur(30px)', zIndex:dir==='fwd'?1:3 }),
                    center: { scale:1, opacity:1, filter:'blur(0px)', zIndex:2 },
                    exit:   (dir) => ({ scale:dir==='fade'?1:(dir==='fwd'?1.4:0.6), opacity:0, filter:'blur(30px)', zIndex:dir==='fwd'?3:1 }),
                  }}
                  initial="enter" animate="center" exit="exit"
                  transition={{ duration:1.1, ease:[0.4,0,0.2,1] }}
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%' }}
                >
                  {activePoint.before && activePoint.after ? (
                    /* BEFORE/AFTER SLIDER VIEW */
                    <div style={{ position: 'absolute', inset: 0 }}>
                      {/* After Image (Background) */}
                      <img src={getUrl(activePoint.after)} alt="After" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0, display: 'block' }} draggable={false} />
                      
                      {/* Before Image (Foreground, Clipped) */}
                      <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
                        <img src={getUrl(activePoint.before)} alt="Before" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
                      </div>
                      
                      {/* Slider Divider Line */}
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`, width: 3, backgroundColor: '#fff', transform: 'translateX(-50%)', pointerEvents: 'none', boxShadow: '0 0 15px rgba(0,0,0,0.7)' }}>
                        {/* Thumb Icon */}
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 36, height: 36, backgroundColor: '#fff', borderRadius: '50%', display:'flex', alignItems:'center', justifyContent:'center', color:'#111', boxShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                          <ChevronLeft size={18} strokeWidth={3} style={{ marginRight: -3 }}/>
                          <ChevronRight size={18} strokeWidth={3} style={{ marginLeft: -3 }}/>
                        </div>
                      </div>

                      {/* Invisible Range Slider */}
                      <input 
                        type="range" min="0" max="100" value={sliderPos} 
                        onChange={(e) => setSliderPos(e.target.value)} 
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'ew-resize', zIndex: 20, margin: 0 }} 
                      />

                      {/* Slider Labels */}
                      <div style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(0,0,0,0.65)', padding: '5px 12px', borderRadius: 8, color: '#FCD34D', fontSize: 11, fontWeight: 800, pointerEvents: 'none', fontFamily:'DM Mono,monospace', border:'1px solid rgba(245,158,11,0.4)', backdropFilter:'blur(4px)', zIndex:15 }}>BEFORE</div>
                      <div style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,0.65)', padding: '5px 12px', borderRadius: 8, color: '#86EFAC', fontSize: 11, fontWeight: 800, pointerEvents: 'none', fontFamily:'DM Mono,monospace', border:'1px solid rgba(34,197,94,0.4)', backdropFilter:'blur(4px)', zIndex:15 }}>AFTER</div>
                    </div>
                  ) : (
                    /* SINGLE IMAGE FALLBACK */
                    <img src={getUrl(activePoint.before || activePoint.after)} alt="Point visual" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} draggable={false} />
                  )}
                </motion.div>
              </AnimatePresence>

              {/* OVERLAYS (Buttons & Nav) */}
              <div style={{ zIndex:40, position:'absolute', inset:0, pointerEvents:'none' }}>
                <button onClick={handleBack} className="asv-back-btn" style={{ pointerEvents:'all' }}>
                  <ChevronLeft size={14} strokeWidth={2.5}/><span>Back to Map</span>
                </button>
                
                {canGoBack && <div style={{ pointerEvents:'all' }}><ArrowBtn side="left"  onClick={e => { e.stopPropagation(); navigate(curIdx-1,'bwd'); }} label={pointList[curIdx-1]?.label||`Point ${curIdx}`}/></div>}
                {canGoFwd  && <div style={{ pointerEvents:'all' }}><ArrowBtn side="right" onClick={e => { e.stopPropagation(); navigate(curIdx+1,'fwd'); }} label={pointList[curIdx+1]?.label||`Point ${curIdx+2}`}/></div>}
                
                <div className="asv-bottom-bar" style={{ pointerEvents:'all' }}>
                  <span style={{ fontWeight:800, color:'#fff', fontSize:13 }}>{activePoint.label || `Point ${activePoint.index}`}</span>
                  <span style={{ fontSize:9, fontWeight:800, letterSpacing:1, padding:'3px 8px', borderRadius:4, textTransform:'uppercase', background:'rgba(14,165,233,0.25)', color:'#7DD3FC' }}>
                    {activePoint.before && activePoint.after ? 'COMPARISON' : 'SINGLE PHOTO'}
                  </span>
                  <span style={{ fontSize:11, color:'rgba(255,255,255,0.45)', fontFamily:'DM Mono,monospace' }}>{curIdx+1} / {pointList.length}</span>
                </div>
              </div>
            </div>

            {/* RIGHT: MINI-MAP SIDEBAR (Bara Size & Human Walking) */}
            <div style={{ width: 450, background: '#080C18', borderLeft: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', zIndex: 45 }}>
              <div style={{ padding: '16px 20px', fontSize: 13, fontWeight: 800, color: '#38BDF8', letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'DM Mono,monospace', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                📍 Live Location
              </div>
              <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                {diagramUrl ? (
                  <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: '100%' }}>
                    {/* Proper objectFit constraints ensure absolute coords match image aspect ratio */}
                    <img src={diagramUrl} alt="mini" style={{ maxWidth: '100%', maxHeight: '520px', opacity: 0.5, display: 'block' }}/>
                    
                    {/* Render standard inactive pins (only those not currently active) */}
                    {pointList.map(pt => {
                      if (pt.id === activePoint.id) return null; // Skip active, rendered below via Framer
                      return (
                        <div key={pt.id} style={{
                          position:'absolute', left:`${pt.x_percent}%`, top:`${pt.y_percent}%`, transform:'translate(-50%,-50%)',
                          width:8, height:8, borderRadius:'50%', background:'rgba(255,255,255,0.25)', zIndex:1,
                        }}/>
                      );
                    })}

                    {/* ACTIVE PIN: Animated Human that walks to new points */}
                    <motion.div
                      initial={false}
                      animate={{ left: `${activePoint.x_percent}%`, top: `${activePoint.y_percent}%` }}
                      transition={{ type: "spring", stiffness: 60, damping: 14 }}
                      style={{
                        position: 'absolute',
                        zIndex: 20,
                        transform: 'translate(-50%, -100%)', // align feet to the exact point
                      }}
                    >
                      <div style={{ 
                        animation: isWalking ? 'asv-humanWalk 0.3s infinite alternate ease-in-out' : 'none',
                        filter: 'drop-shadow(0 5px 8px rgba(0,0,0,0.8))' 
                      }}>
                        <WalkingHuman color="#38BDF8" size={38} />
                      </div>
                    </motion.div>
                  </div>
                ) : (
                  <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No Diagram Available</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const STYLES = `
  .mj-toolbar { display:flex; align-items:center; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
  .mj-search-wrap { position:relative; flex:1; max-width:320px; }
  .mj-search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:rgba(255,255,255,0.25); pointer-events:none; }
  .mj-search { width:100%; padding:9px 14px 9px 38px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E2E8F0; font-family:'DM Sans',sans-serif; font-size:13px; outline:none; transition:border-color 0.2s; box-sizing:border-box; }
  .mj-search:focus { border-color:rgba(14,165,233,0.35); }
  .mj-search::placeholder { color:rgba(255,255,255,0.20); }
  .mj-filter-select { padding:9px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E2E8F0; font-family:'DM Sans',sans-serif; font-size:13px; outline:none; cursor:pointer; }
  .mj-filter-select option { background:#1E2535; }

  .mj-stats-strip { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
  .mj-stat-chip { display:flex; align-items:center; gap:7px; padding:8px 14px; border-radius:10px; font-size:12px; font-weight:700; font-family:'DM Mono',monospace; border:1px solid; }
  .mj-stat-chip-val { font-size:18px; font-weight:800; letter-spacing:-0.03em; }

  .mj-table-wrap { background:#0D1120; border:1px solid rgba(255,255,255,0.06); border-radius:14px; overflow:hidden; margin-bottom:20px; }
  .mj-status { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:20px; font-size:10px; font-weight:700; font-family:'DM Mono',monospace; }
  .mj-status-dot { width:5px; height:5px; border-radius:50%; }
  .mj-status-scheduled   { background:rgba(14,165,233,0.12);  color:#38BDF8; }  .mj-status-scheduled .mj-status-dot   { background:#38BDF8; }
  .mj-status-in_progress { background:rgba(245,158,11,0.12);  color:#FCD34D; }  .mj-status-in_progress .mj-status-dot { background:#FCD34D; }
  .mj-status-completed   { background:rgba(34,197,94,0.12);   color:#86EFAC; }  .mj-status-completed .mj-status-dot   { background:#86EFAC; }
  .mj-status-cancelled   { background:rgba(239,68,68,0.10);   color:#FCA5A5; }  .mj-status-cancelled .mj-status-dot   { background:#FCA5A5; }
  .mj-row-actions { display:flex; align-items:center; gap:6px; opacity:0; transition:opacity 0.15s; }
  tr:hover .mj-row-actions { opacity:1; }

  .mj-panel-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.60); backdrop-filter:blur(5px); z-index:200; }
  
  /* UPDATE: Extra Wide Panel */
  .mj-panel { position:fixed; top:0; right:0; bottom:0; width:1100px; max-width:100vw; background:#0D1120; border-left:1px solid rgba(255,255,255,0.08); box-shadow:-24px 0 80px rgba(0,0,0,0.55); z-index:201; display:flex; flex-direction:column; animation:slideIn 0.22s cubic-bezier(0.4,0,0.2,1); overflow:hidden; }
  
  @keyframes slideIn { from{transform:translateX(100%);opacity:0;} to{transform:translateX(0);opacity:1;} }
  .mj-panel-header { padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.06); display:flex; align-items:center; gap:12px; flex-shrink:0; }
  .mj-panel-title  { font-size:16px; font-weight:800; color:#F1F5F9; flex:1; }
  .mj-panel-sub    { font-size:11px; color:rgba(255,255,255,0.28); margin-top:2px; }
  .mj-panel-tabs   { display:flex; gap:4px; padding:0 24px; border-bottom:1px solid rgba(255,255,255,0.06); flex-shrink:0; overflow-x:auto; }
  .mj-panel-tab    { padding:12px 14px; font-size:12px; font-weight:700; color:rgba(255,255,255,0.35); cursor:pointer; border:none; background:none; white-space:nowrap; border-bottom:2px solid transparent; transition:all 0.18s; font-family:'DM Sans',sans-serif; }
  .mj-panel-tab:hover { color:rgba(255,255,255,0.65); }
  .mj-panel-tab.active { color:#0EA5E9; border-bottom-color:#0EA5E9; }
  .mj-panel-body { flex:1; overflow-y:auto; padding:22px 24px; }

  .mj-info-row { display:flex; align-items:flex-start; gap:10px; padding:11px 0; border-bottom:1px solid rgba(255,255,255,0.04); font-size:13px; color:rgba(255,255,255,0.55); }
  .mj-info-row:last-child { border-bottom:none; }
  .mj-info-label { width:100px; flex-shrink:0; font-size:9px; font-weight:700; color:rgba(255,255,255,0.22); font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; padding-top:1px; }

  .mj-string-flags-section { margin-top:24px; padding-top:20px; border-top:1px solid rgba(255,255,255,0.06); }
  .mj-string-flag-chip { display:inline-flex; align-items:center; gap:7px; padding:7px 14px; border-radius:20px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; cursor:pointer; transition:all 0.18s; border:1.5px solid; }
  .mj-flag-warning-bar { display:flex; align-items:center; gap:8px; padding:9px 13px; border-radius:9px; background:rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.20); margin-bottom:14px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; color:#FCA5A5; }

  .mj-pdf-btn { display:flex; align-items:center; gap:6px; padding:7px 14px; border-radius:9px; background:rgba(14,165,233,0.10); border:1px solid rgba(14,165,233,0.25); color:#38BDF8; font-family:'DM Sans',sans-serif; font-size:11px; font-weight:700; cursor:pointer; transition:all 0.18s; white-space:nowrap; flex-shrink:0; }
  .mj-pdf-btn:hover { background:rgba(14,165,233,0.20); border-color:rgba(14,165,233,0.45); }
  .mj-pdf-btn:disabled { opacity:0.45; cursor:not-allowed; }
  .mj-pdf-btn.loading { animation:mj-pdf-pulse 1.2s ease-in-out infinite; }
  @keyframes mj-pdf-pulse { 0%,100%{opacity:0.55;} 50%{opacity:1;} }

  .sld-image-container { position:relative; display:inline-block; max-width:100%; max-height:100%; }
  .gta-map-img { max-width:100%; max-height:600px; display:block; object-fit:contain; }
  .mj-svg-overlay { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible; }
  .mj-string-centroid-label { position:absolute; transform:translate(-50%,-50%); font-size:9px; font-weight:800; font-family:'DM Mono',monospace; border-radius:4px; padding:2px 7px; white-space:nowrap; background:rgba(0,0,0,0.70); backdrop-filter:blur(4px); pointer-events:none; z-index:10; }
  @keyframes flagPulse { 0%,100%{opacity:0.80;} 50%{opacity:1;} }
  .mj-string-flagged-polygon { animation:flagPulse 2s ease-in-out infinite; }

  .asv-back-btn { position:absolute; top:16px; left:16px; z-index:40; display:flex; align-items:center; gap:7px; padding:7px 15px; border-radius:10px; background:rgba(0,0,0,0.70); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.18); color:#fff; font-weight:700; font-size:12px; cursor:pointer; transition:all 0.2s; font-family:'DM Sans',sans-serif; }
  .asv-back-btn:hover { background:rgba(14,165,233,0.28); }
  .asv-bottom-bar { position:absolute; bottom:18px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:10px; z-index:40; background:rgba(0,0,0,0.75); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.12); padding:7px 16px; border-radius:12px; }

  /* UPDATED WALKING ANIMATION */
  @keyframes asv-humanWalk { 
    0% { transform: translateY(0) rotate(-6deg); } 
    100% { transform: translateY(-5px) rotate(6deg); } 
  }
  @keyframes asv-pinPulse { 0%,100%{box-shadow:0 0 0 0 rgba(34,211,165,0.5);} 60%{box-shadow:0 0 0 10px rgba(34,211,165,0);} }
  @keyframes asv-fadeUp { from{opacity:0;transform:translateX(-50%) translateY(6px);} to{opacity:1;transform:translateX(-50%) translateY(0);} }

  .mj-diagram-wrap { position:relative; border-radius:10px; overflow:hidden; border:1px solid rgba(14,165,233,0.18); margin-bottom:16px; }
  .mj-diagram-img  { width:100%; display:block; user-select:none; }

  .mj-annot-item { padding:14px; border-radius:10px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); margin-bottom:8px; }
  .mj-annot-header { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
  .mj-annot-type-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
  .mj-annot-point { font-size:10px; font-weight:700; color:rgba(255,255,255,0.40); font-family:'DM Mono',monospace; flex:1; }
  .mj-annot-fo    { font-size:10px; color:rgba(255,255,255,0.25); font-family:'DM Mono',monospace; }
  .mj-annot-text  { font-size:13px; color:rgba(255,255,255,0.65); line-height:1.55; margin-bottom:8px; }
  .mj-annot-photo { width:100%; border-radius:8px; object-fit:cover; max-height:200px; border:1px solid rgba(255,255,255,0.06); }

  .mj-fsr-card { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:12px; padding:18px; margin-bottom:16px; }
  .mj-fsr-sig  { width:100%; border-radius:8px; border:1px solid rgba(255,255,255,0.06); background:#fff; margin-top:10px; max-height:120px; object-fit:contain; }
  .mj-fsr-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px; }
  .mj-fsr-field { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:8px; padding:10px 12px; }
  .mj-fsr-field-label { font-size:9px; font-weight:700; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; color:rgba(255,255,255,0.22); margin-bottom:4px; }
  .mj-fsr-field-val { font-size:13px; font-weight:600; color:#E2E8F0; }
  .mj-fsr-bool-yes { color:#86EFAC; } .mj-fsr-bool-no { color:#FCA5A5; }

  .mj-pagination { display:flex; align-items:center; justify-content:center; gap:8px; padding:16px 0; }
  .mj-page-btn { width:32px; height:32px; border-radius:8px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); color:rgba(255,255,255,0.45); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; transition:all 0.18s; font-family:'DM Mono',monospace; }
  .mj-page-btn:hover { background:rgba(14,165,233,0.10); color:#38BDF8; }
  .mj-page-btn.active { background:linear-gradient(135deg,rgba(14,165,233,0.20),rgba(245,158,11,0.12)); color:#E2E8F0; border-color:rgba(14,165,233,0.25); }
  .mj-page-info { font-size:11px; color:rgba(255,255,255,0.28); font-family:'DM Mono',monospace; }
`;

const PAGE_SIZE = 12;

const StatusBadge = ({ status }) => (
  <span className={`mj-status mj-status-${status}`}>
    <span className="mj-status-dot"/>
    {status.replace('_', ' ')}
  </span>
);

const FsrBool = ({ label, value }) => (
  <div className="mj-fsr-field">
    <div className="mj-fsr-field-label">{label}</div>
    <div className={`mj-fsr-field-val ${value ? 'mj-fsr-bool-yes' : 'mj-fsr-bool-no'}`}>
      {value ? '✓ Yes' : '✗ No'}
    </div>
  </div>
);

const MideastJobs = () => {
  const [jobs,         setJobs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth,  setFilterMonth]  = useState('');
  const [page,         setPage]         = useState(1);

  const [selected,     setSelected]     = useState(null);
  const [detail,       setDetail]       = useState(null);
  const [detLoading,   setDetLoading]   = useState(false);
  const [panelTab,     setPanelTab]     = useState('info');

  const [layerNotes,   setLayerNotes]   = useState(true);
  const [layerPhotos,  setLayerPhotos]  = useState(true);
  const [layerAnnots,  setLayerAnnots]  = useState(true);

  const [photos,       setPhotos]       = useState([]);
  const [annotations,  setAnnotations]  = useState([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  const [jobStrings,         setJobStrings]         = useState([]);
  const [jobStringFlags,     setJobStringFlags]     = useState([]);
  const [jobStringFlagNotes, setJobStringFlagNotes] = useState({});
  const [savingFlags,        setSavingFlags]        = useState(false);
  const [flagsSaved,         setFlagsSaved]         = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!detail || pdfLoading) return;
    setPdfLoading(true);
    try {
      await generateJobPDF({
        detail,
        jobStrings,
        jobStringFlags,
        annotations,
        photos,
        baseUrl: BASE ? BASE.replace('/api', '') : '',
      });
    } catch (err) {
      console.error('[PDF] generation error:', err);
      alert('PDF generation failed. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BASE}/mideast/jobs?`;
      if (filterMonth) url += `month=${filterMonth}&`;
      const res = await axios.get(url, getAuthHeader());
      setJobs(res.data.jobs || []);
      setPage(1);
    } catch (err) {
      console.error('[Jobs] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openDetail = useCallback(async (job) => {
    setSelected(job);
    setDetail(null);
    setPanelTab('info');
    setPhotos([]);
    setAnnotations([]);
    setPhotosLoaded(false);
    setJobStrings([]);
    setJobStringFlags([]);
    setJobStringFlagNotes({});
    setFlagsSaved(false);
    setPdfLoading(false);
    setDetLoading(true);
    try {
      const [jobRes, fsrRes] = await Promise.all([
        axios.get(`${BASE}/mideast/jobs/${job.id}`,     getAuthHeader()),
        axios.get(`${BASE}/mideast/fsrs/job/${job.id}`, getAuthHeader()),
      ]);
      const jobDetail = { ...jobRes.data, fsr: fsrRes.data.fsr || null };
      setDetail(jobDetail);

      if (jobDetail.diagram?.id) {
        const [stringsSettled, flagsSettled] = await Promise.allSettled([
          axios.get(`${BASE}/mideast/strings/diagram/${jobDetail.diagram.id}`, getAuthHeader()),
          axios.get(`${BASE}/mideast/strings/job/${job.id}/flags`,             getAuthHeader()),
        ]);
        if (stringsSettled.status === 'fulfilled') setJobStrings(stringsSettled.value.data.strings || []);
        if (flagsSettled.status === 'fulfilled') {
          const flags = flagsSettled.value.data.flags || [];
          setJobStringFlags(flags.map(f => f.string_id));
          setJobStringFlagNotes(Object.fromEntries(flags.map(f => [f.string_id, f.note || ''])));
        }
      }
    } catch (err) {
      console.error('[Jobs] detail error:', err);
    } finally {
      setDetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (panelTab !== 'visual' || !selected || photosLoaded) return;
    Promise.all([
      axios.get(`${BASE}/mideast/point-photos/job/${selected.id}`,   getAuthHeader()),
      axios.get(`${BASE}/mideast/fo-annotations/job/${selected.id}`, getAuthHeader()),
    ]).then(([photosRes, annotRes]) => {
      setPhotos(photosRes.data.photos         || []);
      setAnnotations(annotRes.data.annotations || []);
      setPhotosLoaded(true);
    }).catch(err => console.error('[Jobs] photos error:', err));
  }, [panelTab, selected, photosLoaded]);

  const handleDelete = async (job, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete job at ${job.site_name}?`)) return;
    try {
      await axios.delete(`${BASE}/mideast/jobs/${job.id}`, getAuthHeader());
      fetchJobs();
      if (selected?.id === job.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${BASE}/mideast/jobs/${selected.id}/status`, { status }, getAuthHeader());
      setSelected(j => ({ ...j, status }));
      setDetail(d => ({ ...d, job: { ...d.job, status } }));
      fetchJobs();
    } catch (err) {
      console.error('[Jobs] status error:', err);
    }
  };

  const toggleStringFlag = (stringId) => {
    setFlagsSaved(false);
    setJobStringFlags(prev => prev.includes(stringId) ? prev.filter(id => id !== stringId) : [...prev, stringId]);
  };

  const saveStringFlags = async () => {
    if (!selected) return;
    setSavingFlags(true);
    try {
      await axios.post(
        `${BASE}/mideast/strings/job/${selected.id}/flags`,
        { flags: jobStringFlags.map(sid => ({ string_id: sid, note: jobStringFlagNotes[sid] || '' })) },
        getAuthHeader()
      );
      setFlagsSaved(true);
    } catch (err) {
      alert('Failed to save attention flags');
    } finally {
      setSavingFlags(false);
    }
  };

  const filtered = jobs.filter(j => {
    const q = search.toLowerCase();
    const matchSearch = j.site_name?.toLowerCase().includes(q) || j.client_name?.toLowerCase().includes(q) || j.team_name?.toLowerCase().includes(q);
    return matchSearch && (!filterStatus || j.status === filterStatus);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = {
    total:      jobs.length,
    scheduled:  jobs.filter(j => j.status === 'scheduled').length,
    inProgress: jobs.filter(j => ['in_progress','reached_site','reached_panels','work_started','work_finished'].includes(j.status)).length,
    completed:  jobs.filter(j => j.status === 'completed').length,
  };

  const monthOptions = (() => {
    const opts = []; const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      opts.push({ value:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label:d.toLocaleDateString('en-GB',{month:'long',year:'numeric'}) });
    }
    return opts;
  })();

  const baseUrl = BASE ? BASE.replace('/api', '') : '';

  return (
    <>
      <style>{STYLES}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div className="md-page-title">Jobs</div>
          <div className="md-page-sub">{jobs.length} total job{jobs.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="md-btn md-btn-ghost" onClick={fetchJobs}><RefreshCw size={13}/> Refresh</button>
      </div>

      <div className="mj-stats-strip">
        {[
          { label:'Total',       val:stats.total,      color:'#38BDF8', border:'rgba(14,165,233,0.20)', bg:'rgba(14,165,233,0.08)' },
          { label:'Scheduled',   val:stats.scheduled,  color:'#38BDF8', border:'rgba(14,165,233,0.20)', bg:'rgba(14,165,233,0.06)' },
          { label:'In Progress', val:stats.inProgress, color:'#FCD34D', border:'rgba(245,158,11,0.20)', bg:'rgba(245,158,11,0.06)' },
          { label:'Completed',   val:stats.completed,  color:'#86EFAC', border:'rgba(34,197,94,0.20)',  bg:'rgba(34,197,94,0.06)'  },
        ].map(s => (
          <div key={s.label} className="mj-stat-chip" style={{ color:s.color, borderColor:s.border, background:s.bg }}>
            <span className="mj-stat-chip-val">{s.val}</span>{s.label}
          </div>
        ))}
      </div>

      <div className="mj-toolbar">
        <div className="mj-search-wrap">
          <Search size={14} className="mj-search-icon"/>
          <input className="mj-search" placeholder="Search jobs…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <select className="mj-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['scheduled','in_progress','completed','cancelled'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        <select className="mj-filter-select" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
          <option value="">All Months</option>
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="md-skeleton" style={{ height:300, borderRadius:14 }}/>
      ) : filtered.length === 0 ? (
        <div className="md-empty"><div className="md-empty-icon"><Briefcase size={28}/></div>No jobs found</div>
      ) : (
        <>
          <div className="mj-table-wrap">
            <table className="md-table">
              <thead><tr><th>Site</th><th>Client</th><th>Team</th><th>Date</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {paged.map(job => (
                  <tr key={job.id} style={{ cursor:'pointer' }} onClick={() => openDetail(job)}>
                    <td style={{ fontWeight:600, color:'#E2E8F0' }}>{job.site_name}</td>
                    <td style={{ color:'rgba(255,255,255,0.45)', fontSize:12 }}>{job.client_name}</td>
                    <td style={{ color:'rgba(255,255,255,0.40)', fontSize:12 }}>{job.team_name||'—'}</td>
                    <td style={{ fontFamily:'DM Mono', fontSize:11, color:'rgba(255,255,255,0.45)' }}>
                      {new Date(job.scheduled_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
                    </td>
                    <td><StatusBadge status={job.status}/></td>
                    <td>
                      <div className="mj-row-actions">
                        <button className="md-btn md-btn-ghost" style={{ padding:'5px 8px' }} onClick={e => { e.stopPropagation(); openDetail(job); }}><Eye size={12}/></button>
                        <button className="md-btn md-btn-danger" style={{ padding:'5px 8px' }} onClick={e => handleDelete(job, e)}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="mj-pagination">
              <button className="mj-page-btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>‹</button>
              {Array.from({length:totalPages},(_,i)=>i+1)
                .filter(p=>p===1||p===totalPages||Math.abs(p-page)<=1)
                .reduce((acc,p,i,arr)=>{ if(i>0&&arr[i-1]!==p-1) acc.push('...'); acc.push(p); return acc; },[])
                .map((p,i) => p==='...' ? <span key={`e${i}`} className="mj-page-info">…</span> : (
                  <button key={p} className={`mj-page-btn${page===p?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
                ))}
              <button className="mj-page-btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>›</button>
              <span className="mj-page-info">{filtered.length} jobs</span>
            </div>
          )}
        </>
      )}

      {/* ── DETAIL PANEL ── */}
      {selected && (
        <>
          <div className="mj-panel-overlay" onClick={() => setSelected(null)}/>
          <div className="mj-panel">

            <div className="mj-panel-header">
              <div style={{ width:42, height:42, borderRadius:12, background:'rgba(14,165,233,0.12)', display:'flex', alignItems:'center', justifyContent:'center', color:'#38BDF8', flexShrink:0 }}>
                <Briefcase size={18} strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="mj-panel-title">{detail?.job?.site_name || selected.site_name}</div>
                <div className="mj-panel-sub">
                  {new Date(selected.scheduled_date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
                </div>
              </div>
              <StatusBadge status={selected.status}/>

              {detail && !detLoading && (
                <button className={`mj-pdf-btn${pdfLoading ? ' loading' : ''}`} onClick={handleDownloadPDF} disabled={pdfLoading} title="Download job report as PDF">
                  <Download size={12} strokeWidth={2.5}/>
                  {pdfLoading ? 'Generating…' : 'PDF'}
                </button>
              )}

              <button className="md-modal-close" onClick={() => setSelected(null)}><X size={18}/></button>
            </div>

            <div className="mj-panel-tabs">
              {[
                { id:'info',   label:'Details' },
                { id:'notes',  label:`Notes${detail?.notes?.length ? ` (${detail.notes.length})` : ''}` },
                { id:'visual', label:'Diagram & Photos' },
                { id:'fsr',    label:'FSR' },
              ].map(tab => (
                <button key={tab.id} className={`mj-panel-tab${panelTab===tab.id?' active':''}`} onClick={() => setPanelTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mj-panel-body">
              {detLoading ? (
                [...Array(5)].map((_,i) => <div key={i} className="md-skeleton" style={{ height:16, marginBottom:14, width:`${50+i*10}%` }}/>)
              ) : detail ? (
                <>
                  {/* ── DETAILS TAB ── */}
                  {panelTab === 'info' && (
                    <>
                      <div style={{ marginBottom:24 }}>
                        {[
                          { label:'Site',     value:detail.job.site_name,        icon:<MapPin size={13}/> },
                          { label:'Client',   value:detail.job.client_name,      icon:<FileText size={13}/> },
                          { label:'Location', value:detail.job.location||'—',    icon:<MapPin size={13}/> },
                          { label:'Team',     value:detail.job.team_name||'Unassigned', icon:<UsersRound size={13}/> },
                          { label:'Date',     value:new Date(detail.job.scheduled_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}), icon:<CalendarDays size={13}/> },
                          { label:'Time',     value:detail.job.scheduled_time||'—', icon:<Clock size={13}/> },
                        ].map(({ label, value, icon }) => (
                          <div key={label} className="mj-info-row">
                            <span className="mj-info-label">{label}</span>
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ color:'rgba(14,165,233,0.55)' }}>{icon}</span>{value}
                            </span>
                          </div>
                        ))}
                      </div>

                      {detail.members?.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12 }}>Team Members</div>
                          {detail.members.map(m => (
                            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                              <div style={{ width:30, height:30, borderRadius:8, background:m.role==='lead'?'rgba(245,158,11,0.14)':'rgba(14,165,233,0.10)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0, color:m.role==='lead'?'#FCD34D':'#38BDF8' }}>
                                {`${m.firstname?.[0]||''}${m.lastname?.[0]||''}`.toUpperCase()}
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:'#E2E8F0' }}>{m.firstname} {m.lastname}</div>
                                {m.mobile && <div style={{ fontSize:10, color:'rgba(255,255,255,0.28)', fontFamily:'DM Mono' }}>{m.mobile}</div>}
                              </div>
                              <span style={{ fontSize:9, fontWeight:700, color:m.role==='lead'?'#FCD34D':'#38BDF8', fontFamily:'DM Mono', letterSpacing:'0.08em' }}>{m.role.toUpperCase()}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div>
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:10 }}>Update Status</div>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {['scheduled','in_progress','completed','cancelled'].map(s => (
                            <button key={s} className={`mj-status mj-status-${s}`}
                              style={{ cursor:'pointer', border:'1px solid', borderColor:detail.job.status===s?'currentColor':'transparent', opacity:detail.job.status===s?1:0.45, padding:'6px 12px', transition:'all 0.18s' }}
                              onClick={() => updateStatus(s)}>
                              <span className="mj-status-dot"/>{s.replace('_',' ')}
                            </button>
                          ))}
                        </div>
                      </div>

                      {jobStrings.length > 0 && (
                        <div className="mj-string-flags-section">
                          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
                            <AlertTriangle size={11} style={{ color:'rgba(239,68,68,0.55)' }}/> String Attention Flags
                          </div>
                          {jobStringFlags.length > 0 && (
                            <div className="mj-flag-warning-bar">
                              <AlertTriangle size={13}/>
                              {jobStringFlags.length} string{jobStringFlags.length > 1 ? 's' : ''} flagged for attention
                            </div>
                          )}
                          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
                            {jobStrings.map(s => {
                              const flagged = jobStringFlags.includes(s.id);
                              return (
                                <button key={s.id} className="mj-string-flag-chip"
                                  onClick={() => toggleStringFlag(s.id)}
                                  style={{ borderColor:flagged?s.color:'rgba(255,255,255,0.12)', background:flagged?`${s.color}22`:'rgba(255,255,255,0.04)', color:flagged?s.color:'rgba(255,255,255,0.38)' }}>
                                  <span style={{ width:9, height:9, borderRadius:2, flexShrink:0, background:flagged?s.color:'rgba(255,255,255,0.18)' }}/>
                                  {flagged && '⚠ '}{s.label}
                                </button>
                              );
                            })}
                          </div>
                          <button className={`md-btn ${flagsSaved ? 'md-btn-ghost' : 'md-btn-primary'}`} style={{ fontSize:11 }} onClick={saveStringFlags} disabled={savingFlags || flagsSaved}>
                            {savingFlags ? <><span className="md-spinner"/> Saving…</> : flagsSaved ? <><CheckCircle2 size={12}/> Flags Saved</> : 'Save Attention Flags'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* ── NOTES TAB ── */}
                  {panelTab === 'notes' && (
                    detail.notes?.length === 0 ? (
                      <div className="md-empty"><div className="md-empty-icon"><MessageSquare size={24}/></div>No assignment notes</div>
                    ) : detail.notes?.map(note => (
                      <div key={note.id} className="mj-annot-item">
                        <div className="mj-annot-header">
                          <div className="mj-annot-type-dot" style={{ background:note.annotation_type_color||'#94A3B8' }}/>
                          <span className="mj-annot-point">{note.point_label||'General Note'}</span>
                          {note.annotation_type_label && (
                            <span className="md-badge" style={{ background:`${note.annotation_type_color}18`, color:note.annotation_type_color, border:`1px solid ${note.annotation_type_color}33`, fontSize:9, padding:'2px 7px' }}>
                              {note.annotation_type_label}
                            </span>
                          )}
                        </div>
                        <div className="mj-annot-text">{note.note}</div>
                        <div style={{ fontSize:10, color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono' }}>
                          {new Date(note.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                        </div>
                      </div>
                    ))
                  )}

                  {/* ── VISUAL TAB ── */}
                  {panelTab === 'visual' && (
                    <>
                      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                        {[
                          { key:'notes',  label:'📋 Notes',    color:'#F59E0B', state:layerNotes,  set:setLayerNotes  },
                          { key:'photos', label:'📷 Photos',   color:'#22D3A5', state:layerPhotos, set:setLayerPhotos },
                          { key:'annots', label:'🔧 FO Flags', color:'#EF4444', state:layerAnnots, set:setLayerAnnots },
                        ].map(l => (
                          <button key={l.key}
                            style={{ padding:'5px 12px', borderRadius:20, fontSize:10, fontWeight:700, fontFamily:'DM Mono', cursor:'pointer', border:'1px solid', transition:'all 0.18s', background:l.state?`${l.color}18`:'rgba(255,255,255,0.04)', color:l.state?l.color:'rgba(255,255,255,0.35)', borderColor:l.state?`${l.color}40`:'rgba(255,255,255,0.08)', opacity:l.state?1:0.45 }}
                            onClick={() => l.set(s => !s)}>{l.label}</button>
                        ))}
                      </div>
                      
                      <AdminSLDViewer
                        diagram={detail.diagram}
                        photos={photos}
                        annotations={annotations}
                        notes={detail.notes}
                        strings={jobStrings}
                        flaggedStrings={jobStringFlags}
                        baseUrl={baseUrl}
                        layerNotes={layerNotes}
                        layerPhotos={layerPhotos}
                        layerAnnots={layerAnnots}
                        photosLoaded={photosLoaded}
                      />
                      
                      <div style={{ display:'flex', gap:14, fontSize:10, color:'rgba(255,255,255,0.30)', fontFamily:'DM Mono', margin:'16px 0 20px', flexWrap:'wrap' }}>
                        {[
                          { color:'#F59E0B', label:'Has Note'  },
                          { color:'#22D3A5', label:'Has Photo' },
                          { color:'#EF4444', label:'FO Flag'   },
                          { color:'rgba(255,255,255,0.28)', label:'No data' },
                        ].map(l => (
                          <span key={l.label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <span style={{ width:8, height:8, borderRadius:'50%', background:l.color, display:'inline-block' }}/>{l.label}
                          </span>
                        ))}
                      </div>
                      {jobStringFlags.length > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 13px', borderRadius:9, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.20)', marginBottom:16, fontSize:11, fontWeight:700, fontFamily:'DM Mono', color:'#FCA5A5' }}>
                          <AlertTriangle size={13}/> {jobStringFlags.length} string{jobStringFlags.length>1?'s':''} require attention on this job
                        </div>
                      )}
                      {photosLoaded && annotations.length > 0 && (
                        <>
                          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12, marginTop:4 }}>
                            FO Field Annotations ({annotations.length})
                          </div>
                          {annotations.map(a => (
                            <div key={a.id} className="mj-annot-item">
                              <div className="mj-annot-header">
                                <div className="mj-annot-type-dot" style={{ background:a.annotation_type_color||'#94A3B8' }}/>
                                <span className="mj-annot-point">{a.point_label}</span>
                                <span className="mj-annot-fo">{a.fo_name}</span>
                              </div>
                              {a.note && <div className="mj-annot-text">{a.note}</div>}
                              {a.photo_url && <img src={safeUrl(baseUrl, a.photo_url)} alt="Annotation" className="mj-annot-photo"/>}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}

                  {/* ── FSR TAB ── */}
                  {panelTab === 'fsr' && (
                    <>
                      {(detail.job?.reached_site_at || detail.job?.work_started_at || detail.job?.work_finished_at ||
                        detail.reached_site_at || detail.work_started_at || detail.work_finished_at) && (
                        <div style={{ marginBottom: 20, padding: 16, borderRadius: 12, background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)' }}>
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(56,189,248,0.60)', fontFamily: 'DM Mono', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            🕐 Tracking Timestamps
                          </div>
                          {[
                            { label: 'Reached Site',   value: detail.job?.reached_site_at   || detail.reached_site_at   },
                            { label: 'Reached Panels', value: detail.job?.reached_panels_at || detail.reached_panels_at },
                            { label: 'Work Started',   value: detail.job?.work_started_at   || detail.work_started_at   },
                            { label: 'Work Finished',  value: detail.job?.work_finished_at  || detail.work_finished_at  },
                            { label: 'Site Exited',    value: detail.job?.site_exited_at    || detail.site_exited_at    },
                          ].filter(r => r.value).map(({ label, value }) => (
                            <div key={label} className="mj-info-row">
                              <span className="mj-info-label">{label}</span>
                              <span style={{ color: '#E2E8F0', fontWeight: 600, fontFamily: 'DM Mono', fontSize: 12 }}>
                                {new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {detail.fsr ? (
                        <div className="mj-fsr-card">
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(34,197,94,0.60)', fontFamily: 'DM Mono', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle2 size={12}/> FSR Submitted
                          </div>
                          {[
                            { label: 'Panels Cleaned',  value: detail.fsr.panels_cleaned ?? '—' },
                            { label: 'Submitted By',    value: detail.fsr.submitted_by_name || '—' },
                            { label: 'Submitted At',    value: detail.fsr.submitted_at ? new Date(detail.fsr.submitted_at).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—' },
                            { label: 'Cable Condition', value: detail.fsr.cable_condition  || '—' },
                            { label: 'Cable Quantity',  value: detail.fsr.cable_quantity   || '—' },
                            { label: 'Panel Brand',     value: detail.fsr.panel_brand      || '—' },
                            { label: 'Alarm Code',      value: detail.fsr.alarm_code       || '—' },
                            { label: 'Shading Details', value: detail.fsr.shading_details  || '—' },
                          ].map(({ label, value }) => (
                            <div key={label} className="mj-info-row">
                              <span className="mj-info-label">{label}</span>
                              <span style={{ color: '#E2E8F0', fontWeight: 600 }}>{value}</span>
                            </div>
                          ))}
                          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)', fontFamily: 'DM Mono', margin: '16px 0 8px' }}>
                            Inspection Results
                          </div>
                          <div className="mj-fsr-grid">
                            <FsrBool label="Panel Damage"      value={normBool(detail.fsr.panel_damage)}/>
                            <FsrBool label="Inverter Alarm"    value={normBool(detail.fsr.inverter_alarm)}/>
                            <FsrBool label="Potential Shading" value={normBool(detail.fsr.potential_shading)}/>
                            <FsrBool label="Rusting"           value={normBool(detail.fsr.rusting)}/>
                            <FsrBool label="Bird Dropping"     value={normBool(detail.fsr.bird_dropping)}/>
                            <FsrBool label="Moss & Debris"     value={normBool(detail.fsr.mos_and_debris)}/>
                            <FsrBool label="Earthing"          value={normBool(detail.fsr.earthing)}/>
                          </div>
                          {detail.fsr.observations && (
                            <div className="mj-info-row">
                              <span className="mj-info-label">Observations</span>
                              <span style={{ color: 'rgba(255,255,255,0.50)', fontSize: 12, lineHeight: 1.55 }}>{detail.fsr.observations}</span>
                            </div>
                          )}
                          {detail.fsr.work_done && (
                            <div className="mj-info-row">
                              <span className="mj-info-label">Work Done</span>
                              <span style={{ color: 'rgba(255,255,255,0.50)', fontSize: 12, lineHeight: 1.55 }}>{detail.fsr.work_done}</span>
                            </div>
                          )}
                          {detail.fsr.signature_url && (
                            <>
                              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)', fontFamily: 'DM Mono', marginTop: 16, marginBottom: 8 }}>
                                Signature
                              </div>
                              <img src={safeUrl(baseUrl, detail.fsr.signature_url)} alt="Signature" className="mj-fsr-sig"/>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="md-empty">
                          <div className="md-empty-icon"><FileText size={24}/></div>
                          FSR not submitted yet
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MideastJobs;